/**
 * Pyodide singleton loader — pulls Pyodide once from CDN, caches the instance
 * so every CodeBlock can run code without reloading the WASM runtime (~10MB).
 *
 * Lazy: only fetches when first .run() is called.
 */

const PYODIDE_VERSION = "0.26.2";
const PYODIDE_CDN = `https://cdn.jsdelivr.net/pyodide/v${PYODIDE_VERSION}/full/`;

let pyodidePromise = null;

const loadScript = (src) =>
  new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = resolve;
    s.onerror = () => reject(new Error("Failed to load Pyodide"));
    document.head.appendChild(s);
  });

const load = async () => {
  await loadScript(`${PYODIDE_CDN}pyodide.js`);
  // window.loadPyodide is injected by the script
  // eslint-disable-next-line no-undef
  return await loadPyodide({ indexURL: PYODIDE_CDN });
};

export const getPyodide = () => {
  if (!pyodidePromise) {
    pyodidePromise = load();
  }
  return pyodidePromise;
};

/**
 * Run python code, return { ok, stdout, stderr, error }.
 * Captures stdout via sys.stdout redirection.
 */
export const runPython = async (code) => {
  let py;
  try {
    py = await getPyodide();
  } catch (e) {
    return { ok: false, stdout: "", stderr: "", error: "Couldn't load the Python runtime. Check your internet connection and try again." };
  }

  // Redirect stdout / stderr to capture print()
  const setup = `
import sys, io
__brimi_buf = io.StringIO()
__brimi_err = io.StringIO()
sys.stdout = __brimi_buf
sys.stderr = __brimi_err
`;
  const teardown = `
sys.stdout = sys.__stdout__
sys.stderr = sys.__stderr__
__brimi_out = __brimi_buf.getvalue()
__brimi_errout = __brimi_err.getvalue()
`;

  try {
    await py.runPythonAsync(setup);
    try {
      await py.runPythonAsync(code);
    } catch (runErr) {
      await py.runPythonAsync(teardown);
      const stdout = py.globals.get("__brimi_out") || "";
      const stderr = py.globals.get("__brimi_errout") || "";
      return {
        ok: false,
        stdout,
        stderr,
        error: String(runErr).split("\n").slice(-3).join("\n"),
      };
    }
    await py.runPythonAsync(teardown);
    const stdout = py.globals.get("__brimi_out") || "";
    const stderr = py.globals.get("__brimi_errout") || "";
    return { ok: true, stdout, stderr, error: null };
  } catch (e) {
    return { ok: false, stdout: "", stderr: "", error: String(e) };
  }
};
